// BallTrail.js
import * as THREE from 'three';
import { Global } from '../common/global';

export default class Trail {
  constructor(
    ballMesh,
    {
      length = 80, // number of trail points
      size = 18.0, // gl_PointSize base
      decay = 0.92, // alpha decay per frame (0..1)
      color = 0xffffff, // trail color
    } = {}
  ) {
    this.ball = ballMesh;
    this.length = length;
    this.size = size;
    this.decay = decay;
    this.color = new THREE.Color(color);

    // buffers
    this.positions = new Float32Array(this.length * 3);
    this.alphas = new Float32Array(this.length);
    this.scales = new Float32Array(this.length);
    this.speeds = new Float32Array(this.length);

    // fill with offscreen values / zeros
    for (let i = 0; i < this.length; i++) {
      const idx = i * 3;
      this.positions[idx] = this.positions[idx + 1] = 0; // offscreen
      this.positions[idx + 2] = 0;
      this.alphas[i] = Math.pow(i / this.length, 1.5);
      this.scales[i] = Math.pow(i / this.length, 0.8);
      this.speeds[i] = 0;
    }

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(this.positions, 3).setUsage(
        THREE.DynamicDrawUsage
      )
    );
    this.geometry.setAttribute(
      'alphaAttr',
      new THREE.BufferAttribute(this.alphas, 1).setUsage(THREE.DynamicDrawUsage)
    );
    this.geometry.setAttribute(
      'aScale',
      new THREE.BufferAttribute(this.scales, 1).setUsage(THREE.DynamicDrawUsage)
    );
    this.geometry.setAttribute(
      'aSpeed',
      new THREE.BufferAttribute(this.speeds, 1).setUsage(THREE.DynamicDrawUsage)
    );

    // Shader material lets us use per-point alpha and round points
    this.material = new THREE.ShaderMaterial({
      transparent: true,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uColor: { value: this.color },
        uPointSize: { value: this.size },
        uSpeed: { value: 0.0 },
      },
      vertexShader: `
        attribute float alphaAttr;
            attribute float aSpeed;
        attribute float aScale;
        uniform float uPointSize;
           varying float vSpeed;
 
        varying float vAlpha;
        varying float vCamDistance;
        void main() {
          vAlpha = alphaAttr;
vSpeed=aSpeed;

vec3 newPos=position;
     vec4 mvPosition = modelViewMatrix * vec4(newPos, 1.0);
    
float viewZ = -mvPosition.z;                     // positive in front of camera
viewZ = max(viewZ, 0.1);                         // avoid zero/negative / too-large sizes
vCamDistance=viewZ;
float sizeAttenuation = (250.0 / (viewZ + 1.0)); // milder attenuation
gl_PointSize = uPointSize * sizeAttenuation * aScale;
gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform vec3 uColor;
        varying float vAlpha;
                varying float vCamDistance;
          varying float vSpeed;
        void main() {
          // make round points
          vec2 coord = gl_PointCoord - 0.5;
          float dist = length(coord);
          if (dist > 0.5) discard;

          float _alpha=(1.-pow(vAlpha*vSpeed,vCamDistance*.025))*vSpeed;
          gl_FragColor = vec4(1.,1.,1.,_alpha);//vec4(uColor * t, vAlpha * t);
        }
      `,
    });

    this.points = new THREE.Points(this.geometry, this.material);
    this.points.frustumCulled = false; // prevent disappearing trail
    this._head = 0; // insertion index
  }

  // call once to add to scene
  addToScene(scene) {
    scene.add(this.points);
  }

  // push current ball position into trail (call every frame)
  updateTrail() {
    const pos = this.ball.position;

    // this.points.lookAt(Global.cameraPos);
    for (let i = 0; i < this.length - 1; i++) {
      const i3 = i * 3;
      const i31 = (i + 1) * 3;
      this.speeds[i] =
        Math.abs(this.positions[i3 + 2] - this.positions[i31 + 2]) * 2;
      this.positions[i3 + 0] = this.positions[i31 + 0];
      this.positions[i3 + 1] = this.positions[i31 + 1];
      this.positions[i3 + 2] = this.positions[i31 + 2];
    }

    const i3 = (this.length - 1) * 3;

    this.speeds[i3] = Math.abs(this.positions[i3 + 2] - pos.z) * 2;

    // this.positions[i3 + 0] = pos.x;
    // this.positions[i3 + 1] = pos.y;
    this.positions[i3 + 2] = pos.z;

    this.positions[i3 + 0] = THREE.MathUtils.lerp(
      this.positions[i3 + 0],
      pos.x,
      Math.min(1, Global.delta * 0.75)
    );
    this.positions[i3 + 1] = THREE.MathUtils.lerp(
      this.positions[i3 + 1],
      pos.y,
      Math.min(1, Global.delta * 0.75)
    );
    this.positions[i3 + 2] = THREE.MathUtils.lerp(
      this.positions[i3 + 2],
      pos.z,
      Math.min(1, Global.delta * 0.75)
    );

    // this.geometry.setAttribute(
    //   'alphaAttr',
    //   new THREE.BufferAttribute(this.alphas, 1).setUsage(THREE.DynamicDrawUsage)
    // );

    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.attributes.alphaAttr.needsUpdate = true;
    this.geometry.attributes.aSpeed.needsUpdate = true;
  }
  updateTracking(updatePositions) {
    this.updatePositions = updatePositions;
  }

  // optional: set color dynamically
  setColor(hexOrColor) {
    this.material.uniforms.uColor.value.set(hexOrColor);
  }

  // dispose when not needed
  dispose(scene) {
    scene.remove(this.points);
    this.geometry.dispose();
    this.material.dispose();
  }
}
